import React from "react";
import {
  GET_PROCESSES,
  GET_CONTRIBUTORS,
  PROCESSES_LOADING,
  PROCESSES_LOADING_FINISHED,
  SHOW_SCROLL_ARROW,
  HIDE_SCROLL_ARROW,
  EXTEND_LIST,
  SELECT_ELEMENT,
  SET_SEARCH_TERM,
  SET_SEARCH_MODE
} from "../actions/types";
import chemicalFormula from "chemical-formula";
import periodicTable from "../PeriodicTable";

const initialState = {
  elements: periodicTable.elements,
  processes: [],
  contributors: [],
  loading: false,
  showScrollArrow: false,
  listLength: 40,
  searchTerm: "",
  searchMode: "all"
};

const addSubscripts = (name, regexSub, regexSup) => {
  return name
    .replace(regexSub, num => "," + num + ",")
    .replace(regexSup, num => "," + num + ",")
    .split(",")
    .map(part => {
      if (regexSub && part.match(regexSub))
        //only add subscript to the last character of the matched string
        return (
          <>
            {part.slice(0, -1)}
            <sub>{part.slice(-1)}</sub>
          </>
        );
      if (regexSup && part.match(regexSup))
        //only add superscript to the first character of the matched string
        return (
          <>
            <sup>{part[0]}</sup>
            {part.slice(1)}
          </>
        );

      return part;
    });
};

export default function(state = initialState, action) {
  switch (action.type) {
    case GET_PROCESSES: {
      let { processes, references } = action.payload;

      processes = processes.map(process => {
        const id = Number(process.process_id);
        const isotropic = process.process_isotropic === "1";
        const material = process.process_material;
        const materialSubscripts = addSubscripts(
          process.process_material,
          /(?!D).[0-9xyz]/g
        );
        const reactants = [
          process.process_reactantA,
          process.process_reactantB,
          process.process_reactantC
        ];
        const reactantsSubscripts = reactants.map(reactant =>
          addSubscripts(reactant, /[0-9x]+/g, /([tsin]Bu|[in]Pr|tAm)/g)
        );
        const note = process.process_note;
        const elements = Object.keys(chemicalFormula(process.process_material));

        const searchString =
          material.toLowerCase() +
          reactants.map(reactant => reactant.toLowerCase()).join("") +
          note.toLowerCase();
        return {
          id,
          material,
          isotropic,
          materialSubscripts,
          reactants,
          reactantsSubscripts,
          note,
          elements,
          searchString,
          references: []
        };
      });

      references.forEach(reference => {
        const process = processes.find(
          process => process.id === Number(reference.process_id)
        );

        if (process)
          process.references.push({
            id: reference.reference_id,
            doi: reference.reference_doi,
            author: reference.reference_author,
            created: reference.reference_created,
            citations: reference.reference_citations
          });
      });

      const elements = periodicTable.elements.map(element => {
        if (element.symbol.match(/^(H|O|N)$/)) {
          return element;
        }
        const elementProcesses = processes.filter(process =>
          process.elements.includes(element.symbol)
        );

        return {
          ...element,
          pureIso: elementProcesses.some(
            process => process.elements.length === 1 && process.isotropic
          ),
          pureAniso: elementProcesses.some(
            process => process.elements.length === 1 && !process.isotropic
          ),
          otherIso: elementProcesses.some(
            process => isOtherMaterial(process.elements) && process.isotropic
          ),
          otherAniso: elementProcesses.some(
            process => isOtherMaterial(process.elements) && !process.isotropic
          ),
          oxygenIso: elementProcesses.some(
            process => process.elements.includes("O") && process.isotropic
          ),
          oxygenAniso: elementProcesses.some(
            process => process.elements.includes("O") && !process.isotropic
          ),
          nitrogenIso: elementProcesses.some(
            process => process.elements.includes("N") && process.isotropic
          ),
          nitrogenAniso: elementProcesses.some(
            process => process.elements.includes("N") && !process.isotropic
          )
        };
      });

      return {
        ...state,
        processes,
        elements,
        loading: false
      };
    }
    case GET_CONTRIBUTORS: {
      const contributorArray = action.payload;
      const contributorObject = {};

      contributorArray.forEach(contribution => {
        if (!contributorObject[contribution.contributor_name]) {
          contributorObject[contribution.contributor_name] = 0;
        }

        contributorObject[contribution.contributor_name] += Number(
          contribution.contributor_num
        );
      });

      const contributors = Object.entries(contributorObject)
        .map(entry => ({
          name: entry[0],
          number: entry[1]
        }))
        .sort((a, b) => b.number - a.number);

      return {
        ...state,
        contributors
      };
    }
    case PROCESSES_LOADING:
      return {
        ...state,
        loading: true
      };
    case PROCESSES_LOADING_FINISHED:
      return {
        ...state,
        loading: false
      };
    case SHOW_SCROLL_ARROW: {
      return {
        ...state,
        showScrollArrow: true
      };
    }
    case HIDE_SCROLL_ARROW: {
      return {
        ...state,
        showScrollArrow: false
      };
    }
    case EXTEND_LIST: {
      return {
        ...state,
        listLength: state.listLength + 20
      };
    }
    case SELECT_ELEMENT: {
      return {
        ...state,
        listLength: initialState.listLength
      };
    }
    case SET_SEARCH_TERM: {
      return {
        ...state,
        searchTerm: action.payload,
        listLength: initialState.listLength
      };
    }
    case SET_SEARCH_MODE: {
      return {
        ...state,
        searchMode: action.payload,
        listLength: initialState.listLength
      };
    }
    default:
      return state;
  }
}

function isOtherMaterial(elements) {
  return elements.filter(element => !element.match(/^(O|N)$/)).length > 1;
}
